import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import debounce from "lodash/debounce";

import { useAppSelector } from "@litres-core/store";
import { useTranslation } from "@litres-libs/i18n";
import { hasWindow } from "@litres/utils";
import { SmartLink } from "@litres-modules/smart-link";
import type { ILowerMenuProps, LowerMenuItemType } from "./types";
import { ELowerMenuItems } from "./constants";
import { trackerClick } from "./utils";
import { MoreButton } from "./MoreButton";
import useHorizontalScroll from "./hooks/useHorizontalScroll";
import { useLowerMenuItems } from "./hooks/useLowerMenuItems";
import styles from "./LowerMenuV2.module.scss";

export const LowerMenuV2 = (props: ILowerMenuProps): JSX.Element => {
  const { openedPopup, setOpenedPopup, isPDA = false, isNarrowHeader } = props;

  const { t } = useTranslation();
  const userId = useAppSelector(state => state.foundationUser.id);
  const isUserAnonymous = userId === 0;

  const { menuItems, defaultMoreItems } = useLowerMenuItems(isPDA);
  const [moreItems, setMoreItems] = useState<LowerMenuItemType[]>([]);
  const outputRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const { handleScroll } = useHorizontalScroll(innerRef);

  const checkElementsOverlap = useCallback(() => {
    if (outputRef.current && innerRef.current) {
      const outputRect = outputRef.current.getBoundingClientRect();
      if (outputRect.width === 0) {
        return;
      }

      const innerElements = innerRef.current.children;
      const overlappedElements = [] as LowerMenuItemType[];

      for (let i = 0; i < innerElements.length - 1; i++) {
        const elementRect = innerElements[i].getBoundingClientRect();
        const leftPos =
          hasWindow && window.innerWidth >= 1583
            ? outputRect.left - 12
            : outputRect.left;
        const rightPos =
          hasWindow && window.innerWidth >= 1583
            ? outputRect.right - 66
            : outputRect.right - 78;
        if (elementRect.left < leftPos || elementRect.right > rightPos) {
          overlappedElements.push(menuItems[i]);
        }
      }
      setMoreItems(overlappedElements);
    }
  }, [menuItems]);

  useEffect(() => {
    // Вызываем проверку при показе меню, в том числе при первом показе
    if (!isNarrowHeader && !isPDA) {
      checkElementsOverlap();
    }
  }, [isNarrowHeader]);

  useEffect(() => {
    if (!isPDA) {
      const debouncedCheckElementsOverlap = debounce(checkElementsOverlap, 300);
      // Вызываем функцию проверки перекрытия при изменении размера окна
      window.addEventListener("resize", debouncedCheckElementsOverlap);

      // Вызываем проверку перекрытия при первой загрузке компонента
      const timeout = setTimeout(checkElementsOverlap, 200);

      // Отписываемся от события при размонтировании компонента
      return () => {
        clearTimeout(timeout);
        window.removeEventListener("resize", debouncedCheckElementsOverlap);
      };
    }
  }, [checkElementsOverlap]);

  const onTracker = (title: string, id: string) => {
    trackerClick(title, id, isUserAnonymous);
  };
  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    title: string,
    id: string,
    isBlock = false
  ) => {
    if (isBlock) {
      event.preventDefault();
      return false;
    }
    onTracker(title, id);
  };

  const disableElement = (searchId: string) => {
    return moreItems.some(({ id }) => id === searchId);
  };

  return (
    <nav
      ref={outputRef}
      className={cn(styles.lowerMenu, {
        [styles.lowerMenu_pda]: isPDA,
      })}
      aria-label={t("Основное меню")}
    >
      <div
        ref={innerRef}
        className={cn(styles.lowerMenu__inner, styles.wrapper, {
          [styles.lowerMenu__inner_pda]: isPDA,
        })}
        onScroll={handleScroll}
        data-testid="lowerMenu_items"
      >
        {menuItems.map(({ title, href, id, isOrange = false }) => {
          const isDisabled = disableElement(id);
          const isOrangePromoCode =
            id === ELowerMenuItems.promoCodes &&
            !menuItems.some(item => item.id === ELowerMenuItems.subscription);

          return (
            <SmartLink
              className={cn(styles.lowerMenu__item, {
                [styles.lowerMenu__item_orange]: isOrange || isOrangePromoCode,
                [styles.lowerMenu__item_disabled]: isDisabled,
                [styles.lowerMenu__item_pda]: isPDA,
              })}
              href={href}
              key={id}
              data-testid="lowerMenu_item"
              tabIndex={isDisabled ? -1 : 0}
              aria-hidden={isDisabled}
              onClick={e => handleMenuItemClick(e, title, id, isDisabled)}
            >
              {t(title)}
            </SmartLink>
          );
        })}
        {!isPDA && (
          <div
            className={styles.lowerMenu__moreButtonWrap}
            data-testid="lowerMenu_moreButtonWrap"
          >
            <MoreButton
              onClick={onTracker}
              openedPopup={openedPopup}
              setOpenedPopup={setOpenedPopup}
              items={[...moreItems, ...defaultMoreItems]}
            />
          </div>
        )}
      </div>
    </nav>
  );
};
